import React from "react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export function StatusBadge({ status }) {
  const map = {
    in_progress: { label: "In Progress", cls: "bg-warning/15 text-warning border-warning/30" },
    completed: { label: "Completed", cls: "bg-success/15 text-success border-success/30" },
  };
  const s = map[status] || map.in_progress;
  return <Badge variant="outline" className={cn("font-medium", s.cls)} data-testid={`status-badge-${status}`}>{s.label}</Badge>;
}

export function ProgressBar({ value, className }) {
  return (
    <div className={cn("h-2 w-full rounded-full bg-muted overflow-hidden", className)}>
      <div
        className="h-full rounded-full bg-accent transition-all duration-500"
        style={{ width: `${value || 0}%` }}
      />
    </div>
  );
}

export function ProgressRing({ value = 0, size = 64, stroke = 6 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (value / 100) * c;
  return (
    <div className="relative" style={{ width: size, height: size }} data-testid="progress-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(var(--muted))" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke="hsl(var(--accent))" strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={off}
          className="transition-all duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center font-mono text-sm font-semibold">
        {value}%
      </div>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, desc, action }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6" data-testid="empty-state">
      {Icon && (
        <div className="flex items-center justify-center h-14 w-14 rounded-full bg-muted mb-4 text-muted-foreground">
          <Icon size={26} />
        </div>
      )}
      <h3 className="font-heading font-semibold text-lg">{title}</h3>
      {desc && <p className="text-muted-foreground text-sm mt-1 max-w-sm">{desc}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function fmtDate(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return "—"; }
}

export function fmtDateTime(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
}

export function fmtSize(bytes) {
  if (!bytes) return "—";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0, n = bytes;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

export const NON_COUNTING = ["info", "table", "heading"];

export function isCountable(item) {
  return !NON_COUNTING.includes(item.input_type || "check");
}

export function itemComplete(item, p) {
  p = p || {};
  const t = item.input_type || "check";
  if (t === "check" || t === "check_with_note") return !!p.checked;
  return !!p.value;
}
